import classNames from 'classnames'
// Types
import { throttle } from 'lodash'
import mapboxgl from 'mapbox-gl'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { useFavoriteActionsStore } from 'src/features/favorites/state/useFavoriteActionsStore'
// Components
import {
    BrewOptions,
    FilterMapButtons,
    FoodOptions,
    IndependentCraftToggle,
    PatronOptions,
    SeatingOptions,
} from 'src/features/Map/components/filters'
import HoursOfOperation from 'src/features/Map/components/filters/HoursOfOperation'
import BreweriesForYouSearch from 'src/features/Map/components/forYou/BreweriesForYouSearch'
import {
    BreweriesCountTitle,
    LocationPopupCard,
    Map,
    MapCard,
} from 'src/features/Map/components/map'
import {
    AddRouteStops,
    RouteStopsList,
    TurnByTurnDirections,
} from 'src/features/Map/components/routes'
// Zustand Stores
import { useFilterStore } from 'src/features/Map/state/filterStore'
import { mapStore } from 'src/features/Map/state/mapStore'
import { useSavedRoutesStore } from 'src/features/Map/state/useSavedRoutesStore'
import {
    addUpdateRadiusLayers,
    addUpdateRouteMarkersFromUserLocation,
    drawMapRoute,
    handleShowFilterState,
    setRouteVisibilityNone,
} from 'src/features/Map/utils/functions'
import { calculateMobileMapHeight } from 'src/features/Map/utils/functions/calculateMobileMapHeight'
import { calculateSimplifiedPolygon } from 'src/features/Map/utils/functions/calculateSimplifiedPolygon'
import useMapFunctions from 'src/features/Map/utils/functions/useMapFunctions'
import { parseUserLocationHeaders } from 'src/features/search/utils/helpers'
import SEOTags from 'src/features/seo/components/SEOTags'
import StructuredData from 'src/features/seo/components/StructuredData'
import { Location } from 'src/types/schema/graphql'

import * as UI from '@brewlogix/blx-ui'
import * as CC from '@chakra-ui/react'

import { mapStyle } from '../../src/util/constants'

const accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN

interface IMapPage {
    userLocation: string
    userCoords: [number, number]
}
const Page: NextPage<IMapPage> = ({ userLocation, userCoords }) => {
    // Filter Store
    const showFiltersDrawer = useFilterStore((state) => state.showFiltersDrawer)
    const setShowFiltersDrawer = useFilterStore((state) => state.setShowFiltersDrawer)
    const setPatronFilterOptions = useFilterStore((state) => state.setPatronFilterOptions)
    const setBrewFilterOptions = useFilterStore((state) => state.setBrewFilterOptions)
    const setFoodFilterOptions = useFilterStore((state) => state.setFoodFilterOptions)
    const setSeatingFilterOptions = useFilterStore((state) => state.setSeatingFilterOptions)
    const stateFilter = useFilterStore((state) => state.stateFilter)
    const brewFilterOptions = useFilterStore((state) => state.brewFilterOptions)
    const patronFilterOptions = useFilterStore((state) => state.patronFilterOptions)
    const foodFilterOptions = useFilterStore((state) => state.foodFilterOptions)
    const seatingFilterOptions = useFilterStore((state) => state.seatingFilterOptions)
    const independentCraftOnly = useFilterStore((state) => state.independentCraftOnly)
    const openNowHours = useFilterStore((state) => state.openNowHours)

    // Map Store
    const setMapReady = mapStore((state) => state.setMapReady)
    const mapView = mapStore((state) => state.mapView)
    const activeTab = mapStore((state) => state.activeTab)
    const setActiveTab = mapStore((state) => state.setActiveTab)
    const locations = mapStore((state) => state.locations)
    const setCurrentLocation = mapStore((state) => state.setCurrentLocation)
    const setUserLocationState = mapStore((state) => state.setUserLocationState)
    const setWildcardText = mapStore((state) => state.setWildcardText)
    const showFilters = mapStore((state) => state.showFilters)
    const fullScreenShowFullMap = mapStore((state) => state.fullScreenShowFullMap)
    const locationQuery = mapStore((state) => state.locationQuery)
    const setLocationQuery = mapStore((state) => state.setLocationQuery)
    const directionCoordinates = mapStore((state) => state.directionCoordinates)
    const mobileDirectionsDrawerOpen = mapStore((state) => state.mobileDirectionsDrawerOpen)
    const setMobileDirectionsDrawerOpen = mapStore((state) => state.setMobileDirectionsDrawerOpen)
    const getAddressFromCoords = mapStore((state) => state.getAddressFromCoords)
    const setDirectionsCoordinates = mapStore((state) => state.setDirectionsCoordinates)
    const getLocationAddresses = mapStore((state) => state.getLocationAddresses)
    const profile = mapStore((state) => state.profile)
    const wildcardText = mapStore((state) => state.wildcardText)
    const mapBounds = mapStore((state) => state.mapBounds)
    const radius = mapStore((state) => state.radius)
    const searchAsMapMoves = mapStore((state) => state.searchAsMapMoves)
    const setResultsLoaded = mapStore((state) => state.setResultsLoaded)
    const mapReady = mapStore((state) => state.mapReady)
    const setLocations = mapStore((state) => state.setLocations)
    const filteredIndependentCraftBreweries = mapStore(
        (state) => state.filteredIndependentCraftBreweries
    )
    const resultsLoaded = mapStore((state) => state.resultsLoaded)
    const userLocationState = mapStore((state) => state.userLocationState)
    const mapRouteBounds = mapStore((state) => state.mapRouteBounds)
    const { getRouteGeoJson, map, throttledSearchLocations } = useMapFunctions()
    const setTitle = useSavedRoutesStore((state) => state.setTitle)
    const setDescription = useSavedRoutesStore((state) => state.setDescription)
    const setActiveSavedRoute = useSavedRoutesStore((state) => state.setActiveSavedRoute)
    const popupData = mapStore((state) => state.popupData)
    const setSearchAsMapMoves = mapStore((state) => state.setSearchAsMapMoves)
    const distanceFromRouteSelection = mapStore((state) => state.distanceFromRouteSelection)
    const stepCoordinates = mapStore((state) => state.stepCoordinates)
    const onLoadCenterCoords = mapStore((state) => state.onLoadCenterCoords)
    const setOnLoadCenterCoords = mapStore((state) => state.setOnLoadCenterCoords)
    const populateFavoriteStore = useFavoriteActionsStore((state) => state.populateFavoriteStore)
    const defaultSearchInput = {
        wildcardText,
        stateFilter,
        foodFilterOptions,
        patronFilterOptions,
        brewFilterOptions,
        seatingFilterOptions,
        searchAsMapMoves,
        stepCoordinates,
        locationHours: {
            currentlyOpen: openNowHours,
        },
    }

    // Router
    const router = useRouter()

    // Refs
    const mapContainer = React.useRef()
    const popupRef = React.useRef(new mapboxgl.Popup({ offset: 15, maxWidth: 'none' }))
    const isFirstRender = React.useRef(true)

    const handleRouteChange = (url: string) => {
        if (
            url !== '/map' &&
            url !== '/map?option=breweries-for-you' &&
            url !== '/map?option=brewery-routes'
        ) {
            setDirectionsCoordinates({
                action: 'reorder',
                reorderedArray: [{ ...directionCoordinates[0] }],
            })
            setActiveSavedRoute(null)
            setTitle(null)
            setDescription(null)
        }
    }

    // UseEffect for map setup to be rendered on first load only
    React.useEffect(() => {
        setLocationQuery(userLocation)
        setCurrentLocation(userLocation)
        setUserLocationState({ lat: userCoords[0], lng: userCoords[1] })
        handleShowFilterState()

        if (map.current) return

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: mapStyle,
            zoom: 10,
            accessToken,
            minZoom: 4,
        })

        map.current.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }))
        map.current.addControl(
            new mapboxgl.NavigationControl({
                showCompass: false,
            }),
            'bottom-right'
        )

        getAddressFromCoords(userCoords[0], userCoords[1])
            .then((res) => {
                if (directionCoordinates?.length === 0) {
                    setDirectionsCoordinates({
                        directionCoordinates: {
                            ...directionCoordinates,
                            lat: userCoords[0],
                            lon: userCoords[1],
                            title: userLocation,
                            placeName: res,
                        },
                        action: 'add',
                    })
                }
            })
            .catch((err) => console.error('Error getting address from coords', err))
        map.current.on('load', () => {
            setMapReady(false)
            let viewport
            if (onLoadCenterCoords) {
                map.current?.panTo([onLoadCenterCoords?.lon, onLoadCenterCoords?.lat], {
                    duration: 100,
                })
                viewport = { longitude: onLoadCenterCoords?.lon, latitude: onLoadCenterCoords?.lat }
            } else {
                map.current.panTo([userCoords[1], userCoords[0]], {
                    duration: 100,
                })
                viewport = { longitude: userCoords[1], latitude: userCoords[0] }
            }
            ;(async () =>
                await throttledSearchLocations({
                    ...defaultSearchInput,
                    bounds,
                    viewport,
                }))().then(() => {
                setResultsLoaded(true)
                setMapReady(true)
            })
            const bounds = map.current.getBounds()
        })

        return () => {
            setPatronFilterOptions([])
            setBrewFilterOptions([])
            setFoodFilterOptions([])
            setSeatingFilterOptions([])
            throttledSearchLocations.cancel()
        }
    }, [])
    React.useEffect(() => {
        if (mapRouteBounds)
            map.current.on('load', () =>
                map?.current?.fitBounds(mapRouteBounds as any, { duration: 0, padding: 30 })
            )
    }, [mapRouteBounds])
    React.useEffect(() => {
        if (router.pathname !== '/map') setOnLoadCenterCoords(null)
    }, [router.pathname])

    React.useEffect(() => {
        if (popupData) {
            const popupNode = document.createElement('div')
            const root = createRoot(popupNode)
            root.render(
                <LocationPopupCard type='MapboxProperties' location={popupData?.location} />
            )
            const { lat, lon } = popupData?.location?.address?.geo
            popupRef.current
                .setLngLat({
                    lon,
                    lat,
                })
                .setDOMContent(popupNode)
                .addTo(map.current)
        }
    }, [popupData])

    React.useEffect(() => {
        if (map.current.isStyleLoaded()) {
            if (activeTab === 'breweries') {
                addUpdateRadiusLayers(map.current)
            }
            //? Commenting this out instead of removing it in case this functionality is needed in the future
            // } else {
            //     addUpdateRoutePolygon(map.current)
            // }
        }
    }, [searchAsMapMoves, radius, distanceFromRouteSelection, activeTab])

    const throttledSearchAddresses = React.useMemo(
        throttle(() => getLocationAddresses, 500),
        [locationQuery]
    )

    React.useEffect(() => {
        if (locationQuery && locationQuery.length >= 3) {
            ;(async () => await throttledSearchAddresses())()
        }
    }, [locationQuery])

    const handleDisplayUpdateRoute = React.useCallback(async () => {
        const searchLocationsAlongRoute = async () => {
            const bounds = map.current.getBounds()
            await throttledSearchLocations({
                ...defaultSearchInput,
                radius,
                viewport: {
                    longitude: directionCoordinates[0]?.lon,
                    latitude: directionCoordinates[0]?.lat,
                },
                bounds,
            }).then(() => {
                setResultsLoaded(true)
            })
        }
        if (map.current) {
            if (activeTab === 'routes') {
                ;(async () => {
                    await getRouteGeoJson(directionCoordinates).then((json) => {
                        if (directionCoordinates?.length > 1) {
                            calculateSimplifiedPolygon(json)
                            drawMapRoute(json, map)
                            //? Commenting this out instead of removing it in case this functionality is needed in the future
                            // addUpdateRoutePolygon(map.current)
                            addUpdateRouteMarkersFromUserLocation(map.current)
                        }
                    })
                })()
                if (directionCoordinates.length > 1) setSearchAsMapMoves(false)
                // Take users to the start point
                if (directionCoordinates.length === 1) {
                    map.current.panTo([directionCoordinates[0].lon, directionCoordinates[0].lat], {
                        duration: 100,
                    })
                    setTimeout(async () => {
                        await searchLocationsAlongRoute()
                    }, 100)
                } else {
                    await searchLocationsAlongRoute()
                }
            } else {
                setSearchAsMapMoves(true)
                setRouteVisibilityNone(map.current)
            }
        }
    }, [activeTab, directionCoordinates, profile])

    // useEffect for drawing the route markers
    React.useEffect(() => {
        if (map.current) handleDisplayUpdateRoute()
        router.events.on('routeChangeComplete', handleRouteChange)
        return () => {
            router.events.off('routeChangeStart', handleRouteChange)
        }
    }, [activeTab, directionCoordinates, profile])

    React.useEffect(() => {
        if (activeTab === 'breweries') {
            independentCraftOnly
                ? setLocations(filteredIndependentCraftBreweries)
                : setLocations(locations)
        }
    }, [independentCraftOnly])

    // The useEffect below holds most of the query logic for the map
    React.useEffect(() => {
        // Use a ref to prevent the useEffect from firing on initial render
        if (!isFirstRender.current) {
            setResultsLoaded(false)
            const bounds = map.current.getBounds()
            ;(async () =>
                await throttledSearchLocations({
                    ...defaultSearchInput,
                    radius,
                    viewport: { longitude: userLocationState.lng, latitude: userLocationState.lat },
                    bounds,
                    distanceFromRouteSelection,
                }).then(() => setResultsLoaded(true)))()
        }
        isFirstRender.current = false
        return () => throttledSearchLocations.cancel()
    }, [
        wildcardText,
        radius,
        distanceFromRouteSelection,
        mapBounds,
        brewFilterOptions,
        patronFilterOptions,
        foodFilterOptions,
        stateFilter,
        seatingFilterOptions,
        searchAsMapMoves,
        independentCraftOnly,
        stepCoordinates,
        openNowHours,
    ])

    const handleTabClick = (tab: string) => {
        setActiveTab(tab || 'all')
        if (tab === 'routes') {
            setWildcardText('')
            router.push('/map?option=brewery-routes', undefined, {
                shallow: true,
            })
        } else {
            router.push('/map?option=breweries-for-you', undefined, {
                shallow: true,
            })
        }
    }
    React.useEffect(() => {
        ;(async () => await populateFavoriteStore(false))()
        const { option } = router.query
        if (option === 'brewery-routes') {
            setActiveTab('routes')
        } else {
            setActiveTab('breweries')
        }
    }, [])

    return (
        <div className='flex flex-col items-center justify-center lg:h-[calc(100vh-96px)]'>
            <StructuredData schemaType='map' />
            <SEOTags schemaType='map' />

            <div className='map relative flex w-full flex-col-reverse items-start justify-stretch overflow-hidden lg:flex-row'>
                <div
                    className={classNames(
                        'map__results-panel lg:min-w-38 relative z-20 order-2 flex h-full w-full gap-x-1 lg:mx-8',
                        fullScreenShowFullMap ? 'w-full max-w-[28rem]' : 'lg:w-auto'
                    )}
                >
                    <div
                        className={classNames(
                            showFilters ? '' : 'hidden',
                            'filters hidden h-auto w-full min-w-[16.5rem] max-w-[16.5rem] items-center overflow-y-scroll py-8 pr-6',
                            fullScreenShowFullMap ? 'hidden' : 'xl:block'
                        )}
                    >
                        {showFilters && (
                            <>
                                <BrewOptions />
                                <PatronOptions />
                                <FoodOptions />
                                <SeatingOptions />
                                <HoursOfOperation />
                                <IndependentCraftToggle />
                            </>
                        )}
                    </div>
                    <div className={classNames('flex w-full flex-col pt-8 md:min-w-[24rem]')}>
                        <BreweriesCountTitle />
                        <div className='search-results__tabs flex w-full flex-row items-center justify-start lg:px-4'>
                            <UI.Button
                                label='BreweriesForYou&trade;'
                                data-cy='menu-breweriesForYou'
                                theme='link'
                                size='md'
                                aria-label='Breweries For You'
                                customClasses={classNames(
                                    'w-full m-0 border-0 after:h-[0.05rem] px-0 font-thin',
                                    activeTab === 'breweries'
                                        ? 'active text-primary after:bg-primary hover:text-success after:h-[0.125rem]'
                                        : 'bg-gray active text-gray-300 after:bg-gray-100'
                                )}
                                onClick={() => {
                                    handleTabClick('breweries')
                                }}
                            />
                            <div className='flex w-full flex-col md:flex-row'>
                                <UI.Button
                                    label='BreweryRoutes&trade;'
                                    data-cy='menu-breweryRoutes'
                                    theme='link'
                                    size='md'
                                    aria-label='Brewery Routes'
                                    customClasses={classNames(
                                        'w-full m-0 border-0 after:h-[0.05rem] px-0 font-thin',
                                        activeTab === 'routes'
                                            ? 'active after:bg-primary text-primary hover:text-success after:h-[0.125rem]'
                                            : 'text-gray-300 active after:bg-gray-300'
                                    )}
                                    onClick={() => handleTabClick('routes')}
                                ></UI.Button>
                            </div>
                        </div>
                        <FilterMapButtons />
                        {activeTab === 'breweries' && (
                            <BreweriesForYouSearch mapRef={map.current} popupRef={popupRef} />
                        )}

                        {activeTab === 'routes' && (
                            <div className='h-full lg:h-fit lg:min-h-fit'>
                                {directionCoordinates && <RouteStopsList />}
                                <AddRouteStops />
                            </div>
                        )}

                        <div
                            className={classNames(
                                'z-1 relative h-full overflow-y-auto px-4',
                                mapView ? 'hide-map-button' : ''
                            )}
                        >
                            <div
                                className={classNames(
                                    'map__results-list z-1 relative flex-col items-center justify-start lg:flex ',
                                    mapView ? 'hidden' : ''
                                )}
                            >
                                {!resultsLoaded && (
                                    <div className='absolute bottom-0 left-0 right-0 top-0 z-40 h-full w-full bg-light/70 pt-8'>
                                        <UI.Loading />
                                    </div>
                                )}
                                {resultsLoaded && locations.length === 0 && (
                                    <UI.Card format='inline'>
                                        <>
                                            <div className='card__image'>
                                                <UI.Icon
                                                    size='lg'
                                                    slug='HeroIcons.Exclamation'
                                                    theme='error'
                                                />
                                            </div>
                                            <div className='card__inner'>
                                                <UI.Heading
                                                    text='Please try another location or remove filters'
                                                    size='h5'
                                                    customClasses='card__title'
                                                />
                                                <UI.Button
                                                    size='xs'
                                                    theme='link'
                                                    label='Search Everywhere'
                                                    onClick={() => {
                                                        setCurrentLocation('Anywhere')
                                                        setLocationQuery('Anywhere')
                                                        map.current.panTo([
                                                            userLocationState.lng,
                                                            userLocationState.lat,
                                                        ])
                                                    }}
                                                />
                                            </div>
                                        </>
                                    </UI.Card>
                                )}

                                {locations.map((loc: Location, index) => {
                                    if (loc !== null) {
                                        return (
                                            <MapCard
                                                key={loc.id}
                                                location={loc}
                                                index={index}
                                                popupRef={popupRef}
                                                mapObj={map.current}
                                            />
                                        )
                                    }
                                    return false
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                <CC.Drawer
                    isOpen={mobileDirectionsDrawerOpen}
                    placement='bottom'
                    onClose={() => setMobileDirectionsDrawerOpen(false)}
                    blockScrollOnMount={true}
                    size='sm'
                >
                    <CC.DrawerOverlay />
                    <CC.DrawerContent>
                        <CC.DrawerHeader justifyContent={'flex-end'}>
                            <CC.DrawerCloseButton />
                        </CC.DrawerHeader>
                        <CC.DrawerBody>
                            <TurnByTurnDirections customClasses='w-full h-full overflow-y-scroll' />
                        </CC.DrawerBody>
                    </CC.DrawerContent>
                </CC.Drawer>
                <CC.Drawer
                    isOpen={showFiltersDrawer}
                    placement='left'
                    onClose={() => setShowFiltersDrawer(false)}
                    blockScrollOnMount={true}
                    size='sm'
                >
                    <CC.DrawerOverlay />
                    <CC.DrawerContent>
                        <CC.DrawerHeader>
                            <p>Filter</p>
                            <CC.DrawerCloseButton />
                        </CC.DrawerHeader>

                        <CC.DrawerBody>
                            {showFiltersDrawer && (
                                <>
                                    <BrewOptions />
                                    <PatronOptions />
                                    <FoodOptions />
                                    <SeatingOptions />
                                    <HoursOfOperation />
                                    <IndependentCraftToggle />
                                </>
                            )}
                        </CC.DrawerBody>
                    </CC.DrawerContent>
                </CC.Drawer>

                <div
                    className={classNames(
                        'mapContainerRef map__map-panel relative h-auto w-full overflow-hidden bg-blue-50 md:rounded-2xl lg:order-10 lg:mr-8 lg:mt-8',
                        !mapView ? 'hidden lg:block' : ''
                    )}
                >
                    {!mapReady && (
                        <UI.Loading
                            customClasses='w-full top-1/2 absolute -translate-y-1/2'
                            size='xl'
                            theme='dark'
                        />
                    )}

                    <div
                        className={classNames(
                            'map-container',
                            !mapReady ? 'opacity-0' : 'opacity-100 duration-200'
                        )}
                        style={{
                            height: calculateMobileMapHeight(),
                        }}
                        ref={mapContainer}
                    />
                    <Map mapRef={map} />
                </div>
            </div>
        </div>
    )
}

export default Page

export async function getServerSideProps({ req }) {
    const city = req.headers && req.headers['x-vercel-ip-city']
    const state = req.headers && req.headers['x-vercel-ip-country-region']
    const coords: [number, number] =
        req.headers['x-vercel-ip-latitude'] && req.headers['x-vercel-ip-longitude']
            ? [req.headers['x-vercel-ip-latitude'], req.headers['x-vercel-ip-longitude']]
            : null

    const userLocationParsed = await parseUserLocationHeaders({
        city,
        state,
        coords,
    })

    return {
        props: {
            userLocation: userLocationParsed?.address,
            userCoords: userLocationParsed?.coords,
        },
    }
}
